import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import useAxiosPost from "../../hooks/useAxiosPost";
import { notifyError } from "../toast/Error";
import { notifySuccess } from "../toast/Success";

type AssignVlanToDealerModalProp = {
  show: boolean;
  handleClose: (isApiCall: boolean) => any;
  intPkUserID: number;
  dealerName: string;
};

const AssignVlanToDealerModal: React.FC<AssignVlanToDealerModalProp> = ({
  show,
  handleClose,
  intPkUserID,
  dealerName,
}) => {
  const [selectedOlt, setSelectedOlt] = useState<string>("");
  const [selectedVlans, setSelectedVlans] = useState<string[]>([]);
  const [olts, setOlts] = useState<any[]>([]);
  const [vlans, setVlans] = useState<any[]>([]);
  const [filteredVlans, setFilteredVlans] = useState<any[]>([]);
  const [vlanSearchTerm, setVlanSearchTerm] = useState<string>("");
  const [showVlanList, setShowVlanList] = useState<boolean>(false);
  const vlanListRef = useRef<HTMLDivElement>(null);

  // API call to get OLTs by dealer
  const [
    getOltsData,
    getOltsResponse,
    oltsError,
    oltsLoading,
    oltsMessage,
    oltsErrorCount,
  ] = useAxiosPost<ResponseProp>("admin/getOltsByDealer");

  // API call to get VLANs by OLT
  const [
    getVlansData,
    getVlansResponse,
    vlansError,
    vlansLoading,
    vlansMessage,
    vlansErrorCount,
  ] = useAxiosPost<VlansByOLTPropForDealer>(
    "vlans/getAllVlanProfileByOltAvailableForDealer"
  );

  // API call to assign VLAN to dealer
  const [
    assignVlanData,
    assignVlanResponse,
    assignVlanError,
    assignVlanLoading,
    assignVlanMessage,
    assignVlanErrorCount,
  ] = useAxiosPost<any>("vlans/createVlanM2MDealer");

  // Load OLTs when modal opens
  useEffect(() => {
    if (show) {
      getOltsData({ intPkUserID: intPkUserID }); // Pass dealer ID to get OLTs for this specific dealer
    } else {
      // Reset form when modal closes
      setSelectedOlt("");
      setSelectedVlans([]);
      setVlans([]);
      setFilteredVlans([]);
      setVlanSearchTerm("");
      setShowVlanList(false);
    }
  }, [show, intPkUserID]);

  // Handle OLTs response
  useEffect(() => {
    if (getOltsResponse !== null) {
      setOlts(
        Array.isArray(getOltsResponse.responseData)
          ? getOltsResponse.responseData
          : []
      );
    }
  }, [getOltsResponse]);

  // Handle VLANs response
  useEffect(() => {
    if (getVlansResponse !== null) {
      const vlansData = Array.isArray(getVlansResponse.responseData)
        ? getVlansResponse.responseData
        : [];
      setVlans(vlansData);
      setFilteredVlans(vlansData);
    }
  }, [getVlansResponse]);

  // Handle VLAN search
  const handleVlanSearch = (searchValue: string) => {
    setVlanSearchTerm(searchValue);

    if (!searchValue.trim()) {
      setFilteredVlans(vlans);
      return;
    }

    const filtered = vlans.filter((vlan: any) => {
      const vlanName = (vlan.vlanName || "").toLowerCase();
      const ispName = (vlan.ispName || "").toLowerCase();
      const searchLower = searchValue.toLowerCase();

      return vlanName.includes(searchLower) || ispName.includes(searchLower);
    });

    setFilteredVlans(filtered);
  };

  // Handle OLT selection change
  const handleOltChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const oltId = e.target.value;
    setSelectedOlt(oltId);
    setSelectedVlans([]); // Reset VLAN selection
    setVlans([]); // Clear VLANs
    setFilteredVlans([]); // Clear filtered VLANs
    setVlanSearchTerm(""); // Reset search term
    setShowVlanList(false); // Close VLAN list

    if (oltId) {
      getVlansData({ intPkOltID: parseInt(oltId), intPkUserID: intPkUserID });
    }
  };

  // Handle VLAN selection
  const handleVlanToggle = (vlanId: string) => {
    setSelectedVlans((prev) => {
      if (prev.includes(vlanId)) {
        return prev.filter((id) => id !== vlanId);
      } else {
        return [...prev, vlanId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedVlans.length === filteredVlans.length) {
      setSelectedVlans([]);
    } else {
      setSelectedVlans(
        filteredVlans.map((vlan) => vlan.intPkVlanID.toString())
      );
    }
  };

  // Handle click outside to close list
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        vlanListRef.current &&
        !vlanListRef.current.contains(event.target as Node)
      ) {
        setShowVlanList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle VLAN assignment
  const handleAssignVlan = async () => {
    if (!selectedOlt || selectedVlans.length === 0) {
      notifyError("Please select both OLT and at least one VLAN");
      return;
    }

    try {
      // Assign all selected VLANs
      const assignments = selectedVlans.map((vlanId) =>
        assignVlanData({
          intPkVlanID: parseInt(vlanId),
          intPkUserID: intPkUserID,
        })
      );

      await Promise.all(assignments);
      notifySuccess(
        `Successfully assigned ${selectedVlans.length} VLAN(s) to ${dealerName}`
      );
    } catch (error) {
      console.error("Error assigning VLANs:", error);
    }
  };

  // Handle successful assignment
  useEffect(() => {
    if (assignVlanResponse !== null) {
      // Reset form before closing
      setSelectedOlt("");
      setSelectedVlans([]);
      setVlans([]);
      setFilteredVlans([]);
      setVlanSearchTerm("");
      setShowVlanList(false);
      handleClose(true);
    }
  }, [assignVlanResponse]);

  // Handle errors
  useEffect(() => {
    if (oltsError !== null && oltsErrorCount !== 0) {
      notifyError(oltsError);
    }
  }, [oltsError, oltsErrorCount]);

  useEffect(() => {
    if (vlansError !== null && vlansErrorCount !== 0) {
      notifyError(vlansError);
    }
  }, [vlansError, vlansErrorCount]);

  useEffect(() => {
    if (assignVlanError !== null && assignVlanErrorCount !== 0) {
      notifyError(assignVlanError);
    }
  }, [assignVlanError, assignVlanErrorCount]);

  return (
    <>
      <Modal
        show={show}
        keyboard={false}
        backdrop="static"
        onHide={() => {
          // Reset form when manually closing
          setSelectedOlt("");
          setSelectedVlans([]);
          setVlans([]);
          setFilteredVlans([]);
          setVlanSearchTerm("");
          setShowVlanList(false);
          handleClose(false);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Assign New VLAN to Dealer: <b>{dealerName}</b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select OLT</Form.Label>
            <Form.Select
              value={selectedOlt}
              onChange={handleOltChange}
              disabled={oltsLoading}
            >
              <option value="">Choose OLT</option>
              {olts.map((olt: any) => (
                <option key={olt.intPkOltID} value={olt.intPkOltID}>
                  {olt.oltName} - {olt.host}
                </option>
              ))}
            </Form.Select>
            {oltsLoading && (
              <small className="text-muted">Loading OLTs...</small>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Select VLANs</Form.Label>
            {!selectedOlt ? (
              <div className="text-muted p-3 border rounded">
                <small>Please select an OLT first</small>
              </div>
            ) : vlansLoading ? (
              <div className="text-muted p-3 border rounded">
                <small>Loading VLANs...</small>
              </div>
            ) : vlans.length === 0 ? (
              <div className="text-muted p-3 border rounded">
                <small>No VLANs available for this OLT</small>
              </div>
            ) : (
              <div
                ref={vlanListRef}
                className="border rounded"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                {/* Search Input */}
                <div className="p-2 border-bottom bg-light">
                  <Form.Control
                    type="text"
                    placeholder="Search VLANs..."
                    value={vlanSearchTerm}
                    onChange={(e) => handleVlanSearch(e.target.value)}
                    size="sm"
                  />
                </div>

                {/* Select All Toggle */}
                {filteredVlans.length > 0 && (
                  <div className="p-2 border-bottom bg-light">
                    <Form.Check
                      type="checkbox"
                      label={`Select All (${selectedVlans.length}/${filteredVlans.length})`}
                      checked={
                        selectedVlans.length === filteredVlans.length &&
                        filteredVlans.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </div>
                )}

                {/* VLAN Checkboxes */}
                {filteredVlans.length > 0 ? (
                  filteredVlans.map((vlan: any) => (
                    <div
                      key={vlan.intPkVlanID}
                      className="p-2 d-flex align-items-center border-bottom"
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedVlans.includes(
                          vlan.intPkVlanID.toString()
                        )
                          ? "#e3f2fd"
                          : "white",
                      }}
                      onClick={() =>
                        handleVlanToggle(vlan.intPkVlanID.toString())
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedVlans.includes(
                          vlan.intPkVlanID.toString()
                        )}
                        onChange={() =>
                          handleVlanToggle(vlan.intPkVlanID.toString())
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="me-3"
                      />
                      <img
                        src={vlan.ispLogoUrl}
                        alt={vlan.ispName}
                        style={{
                          width: "20px",
                          height: "20px",
                          marginRight: "8px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <div className="flex-grow-1">
                        <div className="fw-bold">{vlan.vlanName}</div>
                        <small className="text-muted">
                          VLAN {vlan.vlan} - {vlan.ispName}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-center text-muted">
                    <small>No VLANs found matching "{vlanSearchTerm}"</small>
                  </div>
                )}
              </div>
            )}
          </Form.Group>

          <div style={{ textAlign: "center" }}>
            <button
              onClick={handleAssignVlan}
              disabled={
                assignVlanLoading || !selectedOlt || selectedVlans.length === 0
              }
              className="button-primary"
              type="button"
            >
              {assignVlanLoading
                ? "Assigning..."
                : `Assign ${selectedVlans.length} VLAN${
                    selectedVlans.length !== 1 ? "s" : ""
                  }`}
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              // Reset form when canceling
              setSelectedOlt("");
              setSelectedVlans([]);
              setVlans([]);
              setFilteredVlans([]);
              setVlanSearchTerm("");
              setShowVlanList(false);
              handleClose(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AssignVlanToDealerModal;
